import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Icon from '../components/shared/Icon';

export default function Login() {
  const { user, signInWithGoogle, familyId, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (loading) return;
    
    if (user) {
      // Navigate to dashboard - OnboardingRoute will check if onboarding is complete
      // and redirect to onboarding if needed
      // Give a small delay to allow Firestore listener to update
      const timeout = setTimeout(() => {
        navigate('/');
      }, 500);
      
      return () => clearTimeout(timeout);
    }
  }, [user, loading, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      toast.success('Welcome! Let\'s get started.');
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error('Failed to sign in. Please try again.');
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center p-4" style={{ marginLeft: 0, width: '100%' }}>
      <div className="max-w-md w-full">
        {/* Logo/Icon */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <img src="/logo512.png" alt="Untraditional" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl font-semibold text-text-primary mb-3 tracking-tight">
            Untraditional
          </h1>
          <p className="text-lg text-text-secondary">
            Design your family's Jewish life and home, your way
          </p>
        </div>

        {/* Sign In Card */}
        <div className="bg-white rounded-card shadow-card border border-border p-8">
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Welcome
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            Sign in to start planning meaningful Jewish moments for your family.
          </p>

          <button
            onClick={handleGoogleSignIn}
            className="w-full bg-white border border-border hover:border-primary rounded-xl px-6 py-3 flex items-center justify-center gap-3 transition-all hover:shadow-md"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="font-semibold text-text-primary">
              Continue with Google
            </span>
          </button>

          <p className="text-xs text-text-secondary text-center mt-6">
            No temple required. No judgment. Just your family's way.
          </p>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="mb-2"><Icon name="🎉" size="lg" className="text-primary" /></div>
            <div className="text-xs text-text-secondary">Joyous, not enforced</div>
          </div>
          <div>
            <div className="mb-2"><Icon name="🗓️" size="lg" className="text-primary" /></div>
            <div className="text-xs text-text-secondary">Fits your life</div>
          </div>
          <div>
            <div className="mb-2"><Icon name="🤝" size="lg" className="text-primary" /></div>
            <div className="text-xs text-text-secondary">Your tribe</div>
          </div>
        </div>
      </div>
    </div>
  );
}
